import moment from 'moment';
import { SchemaDate, SchemaLocation, ZodArrayToEnum } from '@axiom/types';
import { z } from 'zod';
import {
  estimatedDurationKeys,
  levelOfExperienceAllowedValues,
  selfServiceAdditionalDetailsForTalentMaxLength,
  selfServiceCountryCodes,
  selfServiceAllowedCountryCodeValues,
  selfServiceCountryCodeExclusions,
  worksiteAllowedValues,
} from '@axiom/const';

export const ProjectNeedsSchema = z.object({
  additionalDetailsForTalent: z
    .string()
    .max(selfServiceAdditionalDetailsForTalentMaxLength)
    .nullish(),
  billingUnitsPerWeek: z.number().gte(5).lte(40),
  companyLocation: SchemaLocation.superRefine((val, context) => {
    const addresses = val.locationAddressComponents?.addresses || [];

    const hasStreetAddress = addresses.some(address =>
      address.types.includes('route')
    );

    if (!hasStreetAddress) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Must be a valid street address',
      });
    }

    const countrySegment = addresses.filter(address =>
      address.types.includes('country')
    );
    const shortCountryCode =
      countrySegment.length > 0 ? countrySegment[0]?.short_name : null;

    if (
      shortCountryCode &&
      !selfServiceAllowedCountryCodeValues.includes(shortCountryCode) &&
      !selfServiceCountryCodeExclusions.includes(shortCountryCode)
    ) {
      const supportedCountries = Object.values(selfServiceCountryCodes);

      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Currently, we only support the following countries: ${supportedCountries.join(', ')}`,
      });
    }
  }),
  endDate: SchemaDate.nullable(),
  estimatedDuration: z.enum(ZodArrayToEnum(estimatedDurationKeys)),
  generalSkills: z.array(z.string()).nullish(),
  industry: z.string().nullish(),
  languageId: z.string().uuid().nullish(),
  legalSkills: z.array(z.string()).nullish(),
  legalTechSkills: z.array(z.string()).nullish(),
  levelOfExperience: z
    .enum(ZodArrayToEnum(levelOfExperienceAllowedValues))
    .nullish(),
  otherFocusAreas: z.array(z.string()).nullish(),
  primaryFocusArea: z.string(),
  positionDescription: z.string(),
  startDate: SchemaDate.superRefine((value, ctx) => {
    const parsed = moment(value, 'YYYY-MM-DD', true);
    if (parsed.isValid() && moment().isAfter(parsed, 'day')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Must be today or after',
      });
    }
  }),
  worksite: z.enum(ZodArrayToEnum(worksiteAllowedValues)),
  dsSuggestedId: z.string().uuid().nullish(),
});

export type ProjectNeeds = z.infer<typeof ProjectNeedsSchema>;
